import React from 'react';
import PropTypes from 'prop-types';
import { backgrounds, colors, rounded } from '../../styles/colorPalette';

/**
 * GradientButton - A modern button with neumorphic style, soft shadows and gradient background
 */
const GradientButton = ({
  children,
  className = '',
  variant = 'primary',
  size = 'md',
  icon = null,
  iconPosition = 'left',
  disabled = false,
  onClick,
  ...props
}) => {
  // Base styles - improved with rounded corners like NeumorphicCard and interactive effects
  const baseStyles = 'inline-flex items-center justify-center font-medium transition-all duration-300 relative overflow-hidden border rounded-xl hover:scale-[1.02] hover:shadow-lg active:scale-[0.98] active:shadow-inner';
  
  // Size styles
  const sizeStyles = {
    xs: 'px-3 py-1.5 text-xs',
    sm: 'px-4 py-2 text-sm',
    md: 'px-5 py-2.5 text-base',
    lg: 'px-6 py-3 text-lg',
    xl: 'px-8 py-4 text-xl'};
  
  // Background and text color based on variant
  const variantStyles = {
    primary: 'bg-white text-black border-gray-200',
    secondary: 'bg-white text-black border-gray-200',
    outline: 'bg-white text-black border-gray-200',
    danger: 'bg-white text-black border-gray-200',
    success: 'bg-white text-black border-gray-200',
    warning: 'bg-white text-black border-gray-200',
    dark: 'bg-gray-700 text-white border-white/50',
    light: 'bg-white text-black border-gray-200'};

  // Disabled style
  const disabledStyles = disabled ? 'opacity-50 cursor-not-allowed' : '';
  
  // Neumorphic shadow and gradient styles
  const getNeumorphicStyle = () => {
    // Default light style
    return {
      boxShadow: '6px 6px 12px rgba(0, 0, 0, 0.2), -6px -6px 12px rgba(255, 255, 255, 1)',
      background: 'linear-gradient(145deg, #ffffff, #f8f9fa)'
    };
  };

  // Get neumorphic style
  const neumorphicStyle = getNeumorphicStyle();

  return (
    <button
      className={`${baseStyles} ${sizeStyles[size]} ${variantStyles[variant]} ${disabledStyles} ${className}`}
      style={neumorphicStyle}
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      data-component-name="GradientButton"
      {...props}
    >
      {icon && iconPosition === 'left' && <span className="mr-2">{icon}</span>}
      {children}
      {icon && iconPosition === 'right' && <span className="ml-2">{icon}</span>}
    </button>
  );
};

GradientButton.propTypes = {
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
  variant: PropTypes.oneOf([
    'primary', 'secondary', 'success', 'warning', 'danger', 'dark', 'light', 'outline'
  ]),
  size: PropTypes.oneOf(['xs', 'sm', 'md', 'lg', 'xl']),
  icon: PropTypes.node,
  iconPosition: PropTypes.oneOf(['left', 'right']),
  disabled: PropTypes.bool,
  onClick: PropTypes.func};

export default GradientButton;
